import { EyeOutlined } from '@ant-design/icons'
import { Button, Form, Input, Modal, Select, TableProps } from 'antd'
import dayjs from 'dayjs'
import { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { apiDeleteOutlet, apiFilterOutlets } from '../../api'
import defaultParams from '../../assets/constants/default_params'
import { CREATE, DETAIL, OUTLET, UPDATE } from '../../assets/constants/route_path'
import ActionEditDelete from '../../components/dropdown/ActionEditDelete'
import FormSearch from '../../components/form'
import CompanySelect from '../../components/outlet/CompanySelect'
import ProvinceDistrictSelect from '../../components/outlet/ProvinceDistrictSelect'
import StatusTag from '../../components/outlet/StatusTag'
import UserSelect, { loadUserMap } from '../../components/outlet/UserSelect'
import CustomTable from '../../components/table'
import { useLoading } from '../../context/LoadingContext'
import { IUser } from '../../models/user.model'
import { IOutlet, OUTLET_STATUS_OPTIONS } from '../../models/outlet.modal'
import { authSelector } from '../../redux/slices/authSlice'
import PermUtils from '../../utils/perm-utils'
import { toastError, toastSuccess } from '../../utils/toast'

const formatDateTime = (v?: string | null) => {
    if (!v) return '—'
    const d = dayjs(v)
    if (!d.isValid() || d.year() < 2000) return '—'
    return d.format('DD/MM/YYYY HH:mm')
}

const SEGMENT_LABELS: Record<string, string> = {
    do_xe: 'Độ xe',
    noi_that: 'Nội thất',
    cham_soc_xe: 'Chăm sóc xe',
    do_choi_xe_hoi: 'Đồ chơi xe hơi',
    sim_data: 'SIM Data',
}

type SearchState = {
    search?: string
    cpCodes?: string[]
    provinces?: string[]
    districts?: string[]
    statuses?: string[]
    ownerUserIds?: number[]
}

export default function OutletPage() {
    const [data, setData] = useState<IOutlet[]>([])
    const [total, setTotal] = useState<number>(0)
    const [currentPage, setCurrentPage] = useState<number>(defaultParams.PAGE)
    const [pageSize, setPageSize] = useState<number>(defaultParams.LIMIT)
    const [search, setSearch] = useState<SearchState>({})
    const [selectedProvinces, setSelectedProvinces] = useState<string[]>([])
    const [selectedDistricts, setSelectedDistricts] = useState<string[]>([])
    const [userMap, setUserMap] = useState<Map<number, IUser>>(new Map())
    const [modalDelete, setModalDelete] = useState<{ data: IOutlet | null, isOpen: boolean }>({ data: null, isOpen: false })

    useEffect(() => {
        let mounted = true
        loadUserMap().then(m => { if (mounted) setUserMap(m) })
        return () => { mounted = false }
    }, [])

    const { setIsLoading } = useLoading()
    const [formSearch] = Form.useForm()
    const { account } = useSelector(authSelector)
    const canAdd = PermUtils.hasAnyAuthority(account.listPermission, [PermUtils.canAddOutlet])
    const canUpdate = PermUtils.hasAnyAuthority(account.listPermission, [PermUtils.canUpdateOutlet])
    const canDelete = PermUtils.hasAnyAuthority(account.listPermission, [PermUtils.canDeleteOutlet])

    const fetchData = useCallback(async () => {
        try {
            setIsLoading(true)
            const resp: any = await apiFilterOutlets({
                search: search.search || undefined,
                cpCodes: search.cpCodes,
                provinces: search.provinces,
                districts: search.districts,
                statuses: search.statuses,
                ownerUserIds: search.ownerUserIds,
                limit: pageSize,
                offset: pageSize * (currentPage - 1),
                orderBy: '"createdAt" desc',
            })
            if (resp?.data == null) {
                setData([])
                setTotal(0)
                return
            }
            setData(resp.data.data ?? [])
            setTotal(Number(resp.data.total ?? 0))
        } catch (error) {
            console.log(error)
            setData([])
            setTotal(0)
        } finally {
            setIsLoading(false)
        }
    }, [setIsLoading, currentPage, pageSize, search])

    useEffect(() => {
        fetchData()
    }, [fetchData])

    const handleSearch = (values: any) => {
        setSearch({
            search: values.search,
            cpCodes: values.cpCodes,
            provinces: selectedProvinces.length ? selectedProvinces : undefined,
            districts: selectedDistricts.length ? selectedDistricts : undefined,
            statuses: values.statuses,
            ownerUserIds: values.ownerUserIds,
        })
        setCurrentPage(defaultParams.PAGE)
    }

    const handleClearSearch = () => {
        formSearch.resetFields()
        setSelectedProvinces([])
        setSelectedDistricts([])
        setSearch({})
        setCurrentPage(defaultParams.PAGE)
    }

    const handleDelete = async () => {
        if (!modalDelete.data?.id) return
        try {
            setIsLoading(true)
            const resp = await apiDeleteOutlet(modalDelete.data.id)
            if (resp.status === 200) {
                toastSuccess('Xoá đại lý thành công!')
                setModalDelete({ data: null, isOpen: false })
                fetchData()
            } else {
                toastError('Có lỗi xảy ra khi xoá!')
            }
        } catch (error) {
            console.log(error)
            toastError('Có lỗi xảy ra khi xoá!')
        } finally {
            setIsLoading(false)
        }
    }

    const columns: TableProps<IOutlet>['columns'] = [
        {
            key: 'name',
            title: 'Tên đại lý',
            dataIndex: 'name',
            fixed: 'left',
            render: (_, r) => <Link to={OUTLET + DETAIL + `?id=${r.id}`}>{r.name || '(không tên)'}</Link>,
        },
        {
            key: 'phone',
            title: 'SĐT',
            dataIndex: 'phone',
            render: v => v || '—',
        },
        {
            key: 'province',
            title: 'Khu vực',
            dataIndex: 'province',
            render: v => v || '—',
        },
        {
            key: 'district',
            title: 'Q/Huyện',
            dataIndex: 'district',
            render: v => v || '—',
        },
        {
            key: 'segment',
            title: 'Lĩnh vực',
            dataIndex: 'segment',
            render: v => SEGMENT_LABELS[v as string] || v || '—',
        },
        {
            key: 'ownerUserName',
            title: 'Sale',
            dataIndex: 'ownerUserName',
            render: (_, r) => {
                if (!r.ownerUserId) return '—'
                if (r.ownerUserName) return r.ownerUserName
                const u = userMap.get(r.ownerUserId)
                return u?.username || u?.full_name || `#${r.ownerUserId}`
            },
        },
        {
            key: 'status',
            title: 'Trạng thái',
            dataIndex: 'status',
            render: v => <StatusTag status={v} />,
        },
        {
            key: 'action',
            title: 'Tác vụ',
            align: 'center',
            fixed: 'right',
            render: (_, r) => (
                <ActionEditDelete
                    canUpdate={canUpdate}
                    canDelete={canDelete}
                    dataUpdate={r}
                    linkUpdate={OUTLET + UPDATE}
                    hanldeDelete={() => setModalDelete({ data: r, isOpen: true })}
                    otherFields={[
                        <Link to={OUTLET + DETAIL + `?id=${r.id}`} key='detail'>
                            <Button style={{ width: 100, background: '#7940ee', color: 'white' }} icon={<EyeOutlined />}>
                                Chi tiết
                            </Button>
                        </Link>,
                    ]}
                />
            ),
        },
    ]

    return (
        <div>
            <FormSearch form={formSearch} handleSearch={handleSearch} handleClearSearch={handleClearSearch}>
                <Form.Item name='search' label='Tìm kiếm' style={{ minWidth: 220 }}>
                    <Input placeholder='Tên / SĐT / mã' allowClear />
                </Form.Item>
                <Form.Item name='cpCodes' label='Công ty' style={{ minWidth: 240 }}>
                    <CompanySelect multiple onlyWithOutlets placeholder='Chọn công ty' />
                </Form.Item>
                <Form.Item label='Khu vực'>
                    <ProvinceDistrictSelect
                        provinces={selectedProvinces}
                        districts={selectedDistricts}
                        onProvincesChange={vs => {
                            setSelectedProvinces(vs)
                            setSelectedDistricts([])
                        }}
                        onDistrictsChange={setSelectedDistricts}
                    />
                </Form.Item>
                <Form.Item name='statuses' label='Trạng thái' style={{ minWidth: 240 }}>
                    <Select mode='multiple' allowClear placeholder='Chọn trạng thái' options={OUTLET_STATUS_OPTIONS} />
                </Form.Item>
                <Form.Item name='ownerUserIds' label='Nhân sự phụ trách' style={{ minWidth: 240 }}>
                    <UserSelect multiple placeholder='Chọn nhân sự' />
                </Form.Item>
            </FormSearch>
            {canAdd && (
                <div style={{ textAlign: 'end', marginBottom: 20 }}>
                    <Link to={OUTLET + CREATE}>
                        <Button type='primary'>+ Thêm đại lý</Button>
                    </Link>
                </div>
            )}
            <CustomTable
                namePage='đại lý'
                rowKey='id'
                columns={columns}
                dataSource={data}
                pageSize={pageSize}
                setPageSize={setPageSize}
                total={total}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
            />
            <Modal
                title='Xoá đại lý'
                open={modalDelete.isOpen}
                onCancel={() => setModalDelete({ data: null, isOpen: false })}
                footer={[
                    <Button key='cancel' onClick={() => setModalDelete({ data: null, isOpen: false })}>Huỷ</Button>,
                    <Button key='ok' onClick={handleDelete} type='primary' danger>Xoá</Button>,
                ]}
            >
                <div>Bạn có chắc muốn xoá đại lý <b>{modalDelete.data?.name}</b>?</div>
            </Modal>
        </div>
    )
}

